/*
Copyright (c) 2012-2026 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
import React, { FC, useEffect, useMemo, useCallback } from "react"
import { Card, Row, Col, Dropdown } from "@duxton-web/ui"
import { MoreVerticalUniversal } from "@duxton-web/icons"
import { useSelector, useDispatch } from "react-redux"
import { actionCreator } from "store/store"
import { AppState } from "store/store.type"
import { SubDepartment } from "types/models/menu"
import { SortableItemList } from "components/common"
import { TroyWebTracker } from "utils/tracking"
import { EventAction, EventCategory } from "constants/tracking"
import { useTranslationsMap } from "common/utils/i18n"
import {
  useMenuV3HierarchiesInfinite,
  flattenInfiniteHierarchies,
} from "queries"
import { FoodmaxapipbEntityType } from "generated"
import { mapHierarchyEntitiesToSubDepartments } from "utils/menuV3Mapper"
import LoadMore from "components/common/LoadMore"
import {
  getItemWithoutPhotoCount,
  isItemWithoutPhotoMode,
} from "utils/menu_helper"
import commonStyles from "styles/common.modules.css"
import { useMenuEditorStatus } from "../../hooks/useMenuEditorStatus"
import { MenuSubDepartmentRowV3 } from "./MenuSubDepartmentRowV3"
import { MenuDepartmentRowLoading } from "./MenuDepartmentRowLoading"
import { MenuPartialError } from "./MenuPartialError"

enum MenuActionKey {
  REARRANGE = "REARRANGE",
  SELECT_MULTIPLE = "SELECT_MULTIPLE",
}

export const MenuOverviewSubDepartmentsV3: FC = () => {
  const dispatch = useDispatch()

  const { isEditAllowed } = useMenuEditorStatus()
  const {
    overview: { department: selectedDepartment },
    merchantID,
    merchantGroupID,
    menuGroupDetail: { menuGroupID },
    itemFilter,
  } = useSelector((state: AppState) => state?.menu)

  const {
    currentData,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    isFetching,
    error,
    refetch,
  } = useMenuV3HierarchiesInfinite(
    {
      parentType: FoodmaxapipbEntityType.Department,
      parentID: selectedDepartment?.itemClassID,
      merchantID,
      merchantGroupID,
    },
    {
      skip: !selectedDepartment?.itemClassID || !merchantID,
    },
  )

  // Flatten all pages and map to SubDepartment[]
  // Use currentData to ensure we only get data for current query args
  // RTK Query creates separate cache keys for different parentID values
  const subDepartments = useMemo(() => {
    const allEntities = flattenInfiniteHierarchies(currentData)
    return mapHierarchyEntitiesToSubDepartments(
      allEntities || [],
      selectedDepartment?.itemClassID,
      selectedDepartment?.itemClassName,
    )
  }, [currentData, selectedDepartment])

  // Filter sub-departments based on itemFilter
  const filteredSubDepartments = useMemo(() => {
    if (isItemWithoutPhotoMode(itemFilter)) {
      return subDepartments.filter((subDepartment: SubDepartment) => {
        return getItemWithoutPhotoCount(subDepartment.items) !== 0
      })
    }
    return subDepartments
  }, [subDepartments, itemFilter])

  const selectedSubDepartment = useSelector(
    (state: AppState) => state?.menu?.overview?.subDepartment,
  )

  // Auto-select first subdepartment when department is selected
  // Clear selection when subdepartments become empty
  useEffect(() => {
    if (!selectedDepartment?.itemClassID || isLoading) {
      return
    }

    if (filteredSubDepartments.length > 0) {
      const previouslySelected = selectedSubDepartment
        ? filteredSubDepartments.find(
            (sd: SubDepartment) =>
              sd.itemClassID === selectedSubDepartment.itemClassID,
          )
        : null

      const departmentMismatch =
        !!selectedSubDepartment?.departmentID &&
        !!selectedDepartment?.itemClassID &&
        selectedSubDepartment.departmentID !== selectedDepartment.itemClassID

      const shouldAutoSelect = !previouslySelected || departmentMismatch

      if (!shouldAutoSelect) {
        return
      }

      // If current Redux selection is not in the loaded list, fall back to first (expected UX).
      dispatch(
        actionCreator.menu.selectSubDepartment(
          previouslySelected ?? filteredSubDepartments[0],
        ),
      )
    } else if (selectedSubDepartment && !isFetching) {
      // Clear only when empty is stable, not while hierarchy is still loading
      dispatch(actionCreator.menu.selectSubDepartment(null))
    }
  }, [
    selectedDepartment,
    isLoading,
    isFetching,
    filteredSubDepartments,
    selectedSubDepartment,
    dispatch,
  ])

  const textMap = useTranslationsMap({
    rearrange: "menu.actions.rearrange",
    selectMultiple: "menu.actions.selectMultiple",
    subCategoriesTitle: "menu.headings.subCategories",
  })

  const isItemSelected = (subDepartment: SubDepartment) =>
    subDepartment.itemClassID === selectedSubDepartment?.itemClassID

  // Load more handler
  const handleLoadMore = () => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage()
    }
  }

  const handleMenuClick = useCallback(
    (key: MenuActionKey) => {
      switch (key) {
        case MenuActionKey.REARRANGE:
          dispatch(
            actionCreator.menu.toggleDrawer({
              showSortSubDepartments: true,
            }),
          )
          break
        case MenuActionKey.SELECT_MULTIPLE:
          dispatch(
            actionCreator.menu.toggleDrawer({
              showItemSelection: true,
            }),
          )
          break
        default:
          break
      }

      TroyWebTracker.sendEvent({
        category: EventCategory.menuOverviewTab,
        action: EventAction.clickSubDepartmentMore,
        labels: {
          ...(merchantID
            ? { STORE_ID: merchantID }
            : {
                MERCHANT_GROUP_ID: merchantGroupID,
                MENU_GROUP_ID: menuGroupID,
              }),
          NUM_SUB_DEPARTMENT: filteredSubDepartments?.length ?? 0,
          VALUE: key,
          IS_PARTIAL_MENU: true,
        },
      })
    },
    [
      dispatch,
      merchantID,
      merchantGroupID,
      menuGroupID,
      filteredSubDepartments,
    ],
  )

  const handleSelectSubDepartment = (
    _: React.MouseEvent<HTMLElement>,
    subDepartment: SubDepartment,
  ) => {
    dispatch(actionCreator.menu.selectSubDepartment(subDepartment))

    TroyWebTracker.sendEvent({
      category: EventCategory.menuOverviewTab,
      action: EventAction.clickSubDepartmentName,
      labels: {
        ...(merchantID
          ? { STORE_ID: merchantID }
          : { MERCHANT_GROUP_ID: merchantGroupID, MENU_GROUP_ID: menuGroupID }),
        SUB_DEPARTMENT_ID: subDepartment.itemClassID,
        IS_PARTIAL_MENU: true,
      },
    })
  }

  const canShowRearrange =
    isEditAllowed &&
    filteredSubDepartments.length > 0 &&
    selectedSubDepartment !== null

  const handleMenuClickWithKey = (key: MenuActionKey) => () =>
    handleMenuClick(key)

  const menuItems = [
    ...((canShowRearrange && [
      {
        key: MenuActionKey.REARRANGE,
        label: textMap.rearrange,
        onClick: handleMenuClickWithKey(MenuActionKey.REARRANGE),
      },
    ]) ||
      []),
    {
      key: MenuActionKey.SELECT_MULTIPLE,
      label: textMap.selectMultiple,
      onClick: handleMenuClickWithKey(MenuActionKey.SELECT_MULTIPLE),
    },
  ]

  const renderRow = (subDepartment: SubDepartment) => {
    return <MenuSubDepartmentRowV3 subDepartment={subDepartment} />
  }

  const renderLoadMore = () => {
    if (hasNextPage && !error) {
      return (
        <LoadMore
          onLoadMore={handleLoadMore}
          component={<MenuDepartmentRowLoading count={3} />}
        />
      )
    }
    return null
  }

  // Update total subdepartments count in Redux for checkbox logic
  useEffect(() => {
    if (
      selectedDepartment?.itemClassID &&
      !isLoading &&
      subDepartments.length > 0
    ) {
      dispatch(
        actionCreator.bulkSelectionsV3.setTotalSubDepartments({
          departmentID: selectedDepartment.itemClassID,
          totalSubDepartments: subDepartments.length,
        }),
      )
      // Also store totalItems for each subdepartment
      subDepartments.forEach((subDept: SubDepartment) => {
        if (subDept.itemClassID && subDept.totalItems !== undefined) {
          dispatch(
            actionCreator.bulkSelectionsV3.setTotalItems({
              subDepartmentID: subDept.itemClassID,
              totalItems: subDept.totalItems,
            }),
          )
        }
      })
    }
  }, [isLoading, subDepartments, dispatch, selectedDepartment])

  // Show loading state when:
  // 1. Initial load (isLoading) - no data for current query args yet
  // 2. Fetching but no currentData - means we're fetching for new query args
  // 3. Error exists but we're fetching (user clicked retry) - show loading immediately
  // Note: RTK Query creates separate cache keys for different parentID values,
  // so currentData will be undefined for new cache keys until fetched
  if (isLoading || (isFetching && !currentData) || (error && isFetching)) {
    return (
      <Card
        className={`${commonStyles.menuTableCard} ${commonStyles.center}`}
        title={
          <Row justify="space-between">
            <Col>{textMap.subCategoriesTitle}</Col>
          </Row>
        }
      >
        <MenuDepartmentRowLoading />
      </Card>
    )
  }

  // Show empty card when no department selected (after loading check)
  // If loading, we show loading above. If not loading and no department, show empty card
  if (!selectedDepartment?.itemClassID) {
    return (
      <Card
        className={`${commonStyles.menuTableCard} ${commonStyles.center}`}
        title={
          <Row justify="space-between">
            <Col>{textMap.subCategoriesTitle}</Col>
          </Row>
        }
      />
    )
  }

  // Show error state - if there's an error and we're not fetching, show only the error
  if (error) {
    return (
      <Card
        className={`${commonStyles.menuTableCard} ${commonStyles.center}`}
        title={
          <Row justify="space-between">
            <Col>{textMap.subCategoriesTitle}</Col>
          </Row>
        }
      >
        <MenuPartialError error={error} onRefetch={refetch} />
      </Card>
    )
  }

  return (
    <Card
      className={`${commonStyles.menuTableCard} ${commonStyles.center}`}
      title={
        <Row justify="space-between">
          <Col>{textMap.subCategoriesTitle}</Col>
          {isEditAllowed && (
            <Col>
              <Dropdown
                data-testid="menu-button-dropdown"
                menu={{ items: menuItems }}
                trigger={["click"]}
              >
                <MoreVerticalUniversal />
              </Dropdown>
            </Col>
          )}
        </Row>
      }
    >
      <SortableItemList
        items={filteredSubDepartments}
        renderRowContent={renderRow}
        renderLoadMore={renderLoadMore}
        isItemSelected={isItemSelected}
        onRowClick={handleSelectSubDepartment}
        useDragHandle
      />
    </Card>
  )
}
